import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import addressApi from "api/landlord/address";
import api from "api/landlord/address";
import { Layout } from "providers/Layout";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  DateInput,
  Input,
  Select
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Loader } from "shared/components/Loader";
import { Subscriber } from "views/rent-reporting-id/types";
import { useForm } from "shared/hooks";
import { getErrorMessage } from "utils/error";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { Redirect } from "shared/components/Redirect";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { subscriberValidations } from "./validations";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { Unit } from "shared/types";
import { SubscribeModal } from "views/renters/components/Header/SubscribeModal";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { loadUnitOptions } from "shared/app/InviteApplicantsModal/api";
import { Row } from "./components/Row";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { Section } from "./components/Section";

const usePropertyController = () => {
  const [property, setProperty] = useState<Property | undefined>();
  const [searchQuery, setSearchQuery] = useState("");

  const [debouncedSearch] = useDebouncedValue(searchQuery, 500);

  const { data: propertiesData, isLoading } = useQuery({
    queryKey: ["PROPERTIES", { property: debouncedSearch }],
    queryFn: () =>
      addressApi
        .getProperties({
          per_page: 40,
          searchTerm: debouncedSearch
        })
        .then((data: { items: Property[] }) => data.items),
    placeholderData: keepPreviousData
  });

  const { data: units } = useQuery({
    queryKey: ["GET_UNITS", String(property?.id)],
    queryFn: () =>
      api
        .getUnitsOfProperty({
          propertyId: property?.id || 0
        })
        .then((data: { items: Unit[] }) => data.items)
  });

  return {
    property,
    setProperty,
    setSearchQuery,
    propertiesData,
    isLoading,
    units
  };
};

export const CreateNewSubscription = () => {
  const { type } = useAuthContext();
  const { renterId } = useParams();
  const [showEnrollConfirmationModal, setShowEnrollConfirmationModal] =
    useState(false);
  const [address, setAddress] = useState("");

  const {
    property,
    propertiesData,
    setProperty,
    setSearchQuery,
    units,
    isLoading
  } = usePropertyController();

  const { values, handleChange, register, onSubmit, isSubmitting } = useForm(
    {
      dob: "",
      ssn: "",
      move_in_date: undefined,
      lease_end_date: undefined,
      lease_month_to_month: false,
      monthly_rent_amount: 0,
      moved_out: false,
      move_out_date: "",
      selected_property: "",
      unit: ""
    },
    subscriberValidations
  );

  const navigate = useNavigate();

  const { data: renter, isFetching } = useQuery({
    queryKey: ["SUBSCRIBER_DETAIL", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}`)
        .then(({ data: renter }: { data: Subscriber }) => {
          handleChange("lease_end_date", () => renter.lease_end_date || "")({});
          handleChange(
            "monthly_rent_amount",
            () => (renter.current_month_payment?.cents || 0) / 100 || ""
          )({});
          handleChange("move_out_date", () => renter.move_out_date || "")({});
          handleChange("unit", () => String(renter.unit_id || ""))({});
          handleChange("selected_property", () => ({
            value: String(renter.property.id),
            label: renter.property.name
          }))({});
          handleChange(
            "lease_month_to_month",
            () => !renter.lease_end_date
          )({});
          handleChange("dob", () => renter.dob || "")({});
          handleChange("ssn", () => renter.ssn || "")({});

          setAddress(renter.address);

          return renter;
        })
        .then((renter) => {
          addressApi.getProperty(renter.property.id).then((property) => {
            setProperty(property);
          });

          return renter;
        })
  });

  const { mutate: reenroll, isPending } = useMutation({
    mutationKey: ["REENROLL", renterId],
    mutationFn: (enrollmentOption: { optIn: boolean; optOut: boolean }) => {
      return instance.post(`/portal/v1/customers/${renterId}/reenroll`, {
        property_id: property ? property.id : values.selected_property.value,
        unit_id: values.unit,
        move_in_date: values.move_in_date,
        lease_end_date: values.lease_month_to_month
          ? undefined
          : values.lease_end_date,
        monthly_rent_amount: parseFloat(values.monthly_rent_amount),
        moved_out_date: values.moved_out ? values.move_out_date : undefined,
        address1: property?.address.address1,
        city: property?.address.city,
        state_or_province: property?.address.state_or_province,
        postal_code: property?.address.postal_code,
        country: property?.address.country,
        lease_month_to_month: values.lease_month_to_month || undefined,
        opt_in: enrollmentOption.optIn,
        opt_out: enrollmentOption.optOut
      });
    },
    onSuccess: () => {
      toast.success("Renter was successfully subscribed ");
      navigate(`/rent-reporting?type=unsubscribed`);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const isSubmitBlocked = Boolean(
    isSubmitting || isLoading || isPending || showEnrollConfirmationModal
  );
  const breadCrumbs = (
    <BreadCrumbs
      crumbs={[
        {
          title: "Rent reporting",
          href: "/rent-reporting"
        },
        {
          title: renter?.name || "",
          href: `/rent-reporting/${renterId}`
        },
        {
          title: "Subscription details",
          href: `/rent-reporting/${renterId}/new-subscription`
        }
      ]}
    />
  );

  const headerLayout = (
    <>
      <div className="border-b border-alpha-40 p-6">{breadCrumbs}</div>
      <div className="flex items-center justify-between border-b border-alpha-100 p-6">
        <h1 className="text-xl font-bold text-content-1000">
          Add subscription details
        </h1>
      </div>
    </>
  );

  if (isFetching)
    return (
      <Layout
        headerClassName="py-0"
        header={headerLayout}
        footer={
          <div className="flex justify-end gap-2">
            <Link to={`/rent-reporting/${renterId}`}>
              <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
                Cancel
              </Button>
            </Link>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.PRIMARY}>
              Subscribe
            </Button>
          </div>
        }
      >
        <div className="flex justify-center">
          <Loader />
        </div>
      </Layout>
    );

  if (type.isSDK) return <Redirect href="/" />;

  return (
    <Layout
      headerClassName="py-0"
      header={headerLayout}
      footer={
        <div className="flex justify-end gap-2">
          <Link to={`/rent-reporting/${renterId}`}>
            <Button size={ButtonSize.BASE} variant={ButtonVariant.BASE}>
              Cancel
            </Button>
          </Link>
          <Button
            size={ButtonSize.BASE}
            variant={ButtonVariant.PRIMARY}
            loading={isSubmitBlocked}
            disabled={isSubmitBlocked || type.isPMS}
            type="submit"
            form="create-new-a-subcription"
          >
            Subscribe
          </Button>
        </div>
      }
    >
      <form
        id="create-new-a-subcription"
        className="animate-appear"
        onSubmit={onSubmit({
          onSuccess: () => {
            if (!property) return toast.error("Property not found");

            setShowEnrollConfirmationModal(true);
          }
        })}
      >
        <Section title="Subscription Details">
          <div className="w-1/2 pr-2">
            <Input id="address" label="Address" hideOptional value={address} />
          </div>

          <Row
            left={
              <Select
                id="property"
                searchable
                label="Property"
                loading={isLoading}
                defaultValue={values.selected_property}
                hideOptional
                onInputChange={(query) => {
                  if (query) setSearchQuery(query);
                  else setSearchQuery("");
                }}
                options={[
                  ...(propertiesData?.map((property: Property) => {
                    return {
                      value: String(property.id),
                      label: property.name
                    };
                  }) || [])
                ]}
                {...register("selected_property", (d) => {
                  const property = propertiesData?.find(
                    (p) => String(p.id) === d.target.value
                  );
                  setProperty(property);
                  setAddress(getShortAddressFromAddress(property?.address));
                  return d.target.value;
                })}
              />
            }
            right={
              <AsyncSelect
                id="unit"
                label="Unit"
                key={property?.id}
                searchable={true}
                value={values.unit}
                // @ts-expect-error
                loadOptions={loadUnitOptions}
                additional={{ page: 1, propertyId: property?.id }}
                onChange={handleChange("unit", (e) => e.target.value)}
                options={[
                  ...(units?.map((unit: Unit) => {
                    return {
                      value: String(unit.id),
                      label: unit.title
                    };
                  }) || [])
                ]}
              />
            }
          />

          <Row
            left={
              <DateInput
                id="move_in_date"
                placeholder="MM/DD/YYYY"
                label="Move-in date"
                hideOptional
                {...register("move_in_date")}
              />
            }
            right={
              <>
                <DateInput
                  id="lease_end_date"
                  placeholder="MM/DD/YYYY"
                  label="Lease end date"
                  hideOptional
                  disabled={values.lease_month_to_month}
                  readOnly={values.lease_month_to_month}
                  {...register("lease_end_date")}
                />
                <Checkbox
                  id="lease_month_to_month"
                  label="It’s month to month / no end date"
                  checked={values.lease_month_to_month}
                  {...register("lease_month_to_month", (v) => v.target.checked)}
                />
              </>
            }
          />

          <div className="money-input-2 w-1/2 pr-2 mt-4">
            <Input
              id="monthly_rent_amount"
              {...register("monthly_rent_amount", (e) => {
                if (/^\d*(\.\d{0,2})?$/.test(e.target.value))
                  return e.target.value;

                return values.monthly_rent_amount;
              })}
              hideOptional
              type="money"
              label="Rent amount"
            />
          </div>
        </Section>

        <Section className="mt-10" title="Personal Details">
          <Row
            left={
              <Input
                id="first_name"
                label="Legal first name"
                readOnly
                hideOptional
                value={renter?.first_name}
              />
            }
            right={
              <Input
                id="last_name"
                label="Legal last name"
                readOnly
                hideOptional
                value={renter?.last_name}
              />
            }
          />

          <Row
            left={
              <Input
                id="email"
                label="Email"
                readOnly
                hideOptional
                value={renter?.email}
              />
            }
            right={
              <Input
                id="phone"
                label="Phone"
                readOnly
                hideOptional
                value={renter?.phone}
                placeholder="(123) 000-000"
                type="tel"
              />
            }
          />

          <div className="mt-4 w-1/2 pr-2">
            <DateInput
              id="dob"
              readOnly
              label="Date of birth"
              hideOptional
              value={renter?.dob}
            />
          </div>
        </Section>
      </form>
      <SubscribeModal
        isOpen={showEnrollConfirmationModal}
        setIsOpen={(opened) => {
          setShowEnrollConfirmationModal(opened);
        }}
        renterName={!renter ? "" : `${renter.first_name} ${renter.last_name}`}
        enrollRenters={reenroll}
      />
    </Layout>
  );
};
